'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Group, Layer, Line, Stage, Text } from 'react-konva';
import { Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';

import type { ParkingLayoutRecord } from '../lib/parkingTypes';

type CameraImageDescriptor = {
  src: string;
  name: string;
  width: number;
  height: number;
};

type CameraAnnotationPoint = {
  x: number;
  y: number;
};

type CameraPolygonType = 'regular' | 'reserved' | 'disabled';

type CameraAnnotationPolygon = {
  id: string;
  name: string;
  color: string;
  type: CameraPolygonType;
  points: CameraAnnotationPoint[];
  completed: boolean;
};

type CameraAnnotationExportPolygon = {
  id: string;
  name: string;
  coordinates: CameraAnnotationPoint[];
  color: string;
  type: CameraPolygonType;
  completed: boolean;
};

type CameraAnnotationExportPayload = {
  image: {
    name: string;
    width: number;
    height: number;
  };
  polygons: CameraAnnotationExportPolygon[];
  exportedAt?: string;
};

type ParkingCameraAnnotatorProps = {
  cityId: string;
  spotId: string;
  isAdmin: boolean;
  image: CameraImageDescriptor;
  initialRecord?: ParkingLayoutRecord | null;
};

const isBrowser = typeof window !== 'undefined';

const POLYGON_TYPE_VALUES = new Set<CameraPolygonType>(['regular', 'reserved', 'disabled']);
const LEGACY_POLYGON_TYPE_MAP: Record<string, CameraPolygonType> = {
  other: 'reserved',
};
const POLYGON_TYPE_OPTIONS: Array<{ value: CameraPolygonType; label: string }> = [
  { value: 'regular', label: 'Miejsce parkingowe' },
  { value: 'disabled', label: 'Miejsce dla niepełnosprawnych' },
  { value: 'reserved', label: 'Zakaz parkowania' },
];
const POLYGON_TYPE_COLORS: Record<CameraPolygonType, string> = {
  regular: '#10B981',
  disabled: '#0EA5E9',
  reserved: '#EF4444',
};
const isCameraPolygonType = (value: unknown): value is CameraPolygonType => typeof value === 'string' && POLYGON_TYPE_VALUES.has(value as CameraPolygonType);
const colorForPolygonType = (type: CameraPolygonType): string => POLYGON_TYPE_COLORS[type] ?? POLYGON_TYPE_COLORS.regular;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const createPolygonId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `poly-${Math.random().toString(36).slice(2, 9)}`;
};

const centroidOf = (points: CameraAnnotationPoint[]): CameraAnnotationPoint | null => {
  if (!points.length) {
    return null;
  }

  if (points.length === 1) {
    return points[0];
  }

  let twiceArea = 0;
  let xAccumulator = 0;
  let yAccumulator = 0;

  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    const cross = current.x * next.y - next.x * current.y;
    twiceArea += cross;
    xAccumulator += (current.x + next.x) * cross;
    yAccumulator += (current.y + next.y) * cross;
  }

  if (twiceArea === 0) {
    const avgX = points.reduce((sum, point) => sum + point.x, 0) / points.length;
    const avgY = points.reduce((sum, point) => sum + point.y, 0) / points.length;
    return { x: avgX, y: avgY };
  }

  const area = twiceArea * 0.5;
  return {
    x: xAccumulator / (6 * area),
    y: yAccumulator / (6 * area),
  };
};

const normalisePolygonEntry = (
  entry: Partial<CameraAnnotationExportPolygon> | null | undefined,
  fallbackName: string,
  image: CameraImageDescriptor,
): CameraAnnotationPolygon | null => {
  if (!entry || !Array.isArray(entry.coordinates)) {
    return null;
  }

  const points: CameraAnnotationPoint[] = entry.coordinates
    .map((point) => {
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
        return null;
      }
      return {
        x: clamp(point.x, 0, image.width),
        y: clamp(point.y, 0, image.height),
      };
    })
    .filter((point): point is CameraAnnotationPoint => Boolean(point));

  if (!points.length) {
    return null;
  }

  const type = (() => {
    if (typeof entry.type === 'string') {
      if (isCameraPolygonType(entry.type)) {
        return entry.type;
      }
      const mapped = LEGACY_POLYGON_TYPE_MAP[entry.type];
      if (mapped) {
        return mapped;
      }
    }
    return 'regular';
  })();
  const color = colorForPolygonType(type);

  return {
    id: entry.id ?? createPolygonId(),
    name: entry.name && entry.name.trim().length > 0 ? entry.name : fallbackName,
    color,
    type,
    points,
    completed: entry.completed ?? true,
  };
};

const extractPolygonsFromRecord = (initialRecord: ParkingLayoutRecord | null | undefined, image: CameraImageDescriptor): CameraAnnotationPolygon[] => {
  if (!initialRecord?.meta) {
    return [];
  }

  const meta = initialRecord.meta as Record<string, unknown>;
  const metaCandidates = [meta.camera, meta.cameraOverlay, meta.cameraLayout, meta.cameraPolygons];

  for (const candidate of metaCandidates) {
    if (!candidate || typeof candidate !== 'object') {
      continue;
    }
    if ('polygons' in candidate && Array.isArray((candidate as { polygons?: unknown }).polygons)) {
      const polygons = (candidate as { polygons: Partial<CameraAnnotationExportPolygon>[] }).polygons
        .map((entry, index) => normalisePolygonEntry(entry, `Parking spot ${index + 1}`, image))
        .filter((entry): entry is CameraAnnotationPolygon => Boolean(entry));

      if (polygons.length) {
        return polygons;
      }
    }
  }

  return [];
};

const buildExportPayload = (polygons: CameraAnnotationPolygon[], image: CameraImageDescriptor, exportedAt?: string): CameraAnnotationExportPayload => ({
  image: {
    name: image.name,
    width: image.width,
    height: image.height,
  },
  polygons: polygons.map((polygon) => ({
    id: polygon.id,
    name: polygon.name,
    coordinates: polygon.points.map((point) => ({ x: point.x, y: point.y })),
    color: polygon.color,
    type: polygon.type,
    completed: polygon.completed,
  })),
  exportedAt,
});

const jsonDownloadFileName = (cityId: string, spotId: string) => `parking-${cityId}-${spotId}-camera-layout.json`;

export function ParkingCameraAnnotator({ cityId, spotId, isAdmin, image, initialRecord }: ParkingCameraAnnotatorProps) {
  const stageRef = useRef<Konva.Stage | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const exportFeedbackTimeoutRef = useRef<number | null>(null);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const [polygons, setPolygons] = useState<CameraAnnotationPolygon[]>([]);
  const [activePolygonId, setActivePolygonId] = useState<string | null>(null);
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);
  const [exportFeedback, setExportFeedback] = useState<string | null>(null);

  const storageKey = useMemo(() => `parking-camera-annotator-${cityId}-${spotId}`, [cityId, spotId]);

  const isEditable = isAdmin;
  const stageWidth = image.width;
  const stageHeight = image.height;
  const maxStageHeight = isEditable ? 520 : 640;
  const [stageScale, setStageScale] = useState(() => Math.max(0.1, Math.min(1, maxStageHeight / stageHeight)));

  const activePolygon = useMemo(
    () => (activePolygonId ? (polygons.find((polygon) => polygon.id === activePolygonId) ?? null) : null),
    [activePolygonId, polygons],
  );
  const isDrawingInProgress = isEditable && Boolean(activePolygon && !activePolygon.completed && activePolygon.points.length > 0);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = image.src;
    const handleLoad = () => setImageElement(img);
    const handleError = () => setImageElement(null);
    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [image.src]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const updateScale = () => {
      const { width: availableWidth } = element.getBoundingClientRect();
      if (!availableWidth || Number.isNaN(availableWidth)) {
        return;
      }

      const maxHeight = maxStageHeight;
      const widthScale = availableWidth / stageWidth;
      const heightScale = maxHeight / stageHeight;
      const nextScale = Math.max(Math.min(widthScale, heightScale), 0.1);
      setStageScale((current) => (Math.abs(current - nextScale) < 0.0001 ? current : nextScale));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [maxStageHeight, stageHeight, stageWidth]);

  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!isBrowser || hasHydratedRef.current) {
      return;
    }

    hasHydratedRef.current = true;

    let loadedPolygons: CameraAnnotationPolygon[] | null = null;

    try {
      const storedRaw = window.localStorage.getItem(storageKey);
      if (storedRaw) {
        const parsed = JSON.parse(storedRaw) as Partial<CameraAnnotationExportPayload>;
        if (parsed && Array.isArray(parsed.polygons)) {
          loadedPolygons = parsed.polygons
            .map((entry, index) => normalisePolygonEntry(entry, `Parking spot ${index + 1}`, image))
            .filter((entry): entry is CameraAnnotationPolygon => Boolean(entry));
        }
      }
    } catch (error) {
      console.warn('Nie udało się odczytać zapisanych danych kamery', error);
    }

    if (!loadedPolygons || loadedPolygons.length === 0) {
      loadedPolygons = extractPolygonsFromRecord(initialRecord ?? null, image);
    }

    if (loadedPolygons && loadedPolygons.length > 0) {
      setPolygons(loadedPolygons);
      setSelectedPolygonId(loadedPolygons[0].id);
      const unfinished = loadedPolygons.find((polygon) => !polygon.completed);
      if (unfinished) {
        setActivePolygonId(unfinished.id);
      }
    }
  }, [image, initialRecord, storageKey]);

  useEffect(() => {
    if (!isBrowser || !hasHydratedRef.current) {
      return;
    }

    const payload = buildExportPayload(polygons, image, new Date().toISOString());
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (error) {
      console.warn('Nie udało się zapisać danych kamery', error);
    }
  }, [polygons, image, storageKey]);

  useEffect(
    () => () => {
      if (exportFeedbackTimeoutRef.current) {
        window.clearTimeout(exportFeedbackTimeoutRef.current);
      }
    },
    [],
  );

  const updatePolygon = useCallback((polygonId: string, updater: (polygon: CameraAnnotationPolygon) => CameraAnnotationPolygon | null) => {
    setPolygons((prev) => {
      let changed = false;
      const next = prev
        .map((polygon) => {
          if (polygon.id !== polygonId) {
            return polygon;
          }
          const updated = updater(polygon);
          if (!updated) {
            changed = true;
            return null;
          }
          changed = changed || updated !== polygon;
          return updated;
        })
        .filter((polygon): polygon is CameraAnnotationPolygon => Boolean(polygon));
      return changed ? next : prev;
    });
  }, []);

  const handleStagePointerDown = useCallback(
    (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
      const targetPolygonId = event?.target?.attrs?.['data-polygon-id'];
      if (typeof targetPolygonId === 'string') {
        const pointIndexAttr = event?.target?.attrs?.['data-point-index'];
        const pointIndex = typeof pointIndexAttr === 'number' ? pointIndexAttr : typeof pointIndexAttr === 'string' ? Number(pointIndexAttr) : null;
        setSelectedPolygonId(targetPolygonId);
        const polygon = polygons.find((entry) => entry.id === targetPolygonId);
        if (polygon) {
          if (isEditable && !polygon.completed && polygon.points.length >= 3 && pointIndex === 0) {
            updatePolygon(polygon.id, (entry) => ({
              ...entry,
              completed: true,
            }));
            setActivePolygonId(null);
            return;
          }
          if (isEditable && !polygon.completed) {
            setActivePolygonId(polygon.id);
          }
        }
        return;
      }

      if (!isEditable) {
        return;
      }

      const stage = stageRef.current;
      if (!stage) {
        return;
      }

      const active = activePolygon && !activePolygon.completed ? activePolygon : null;
      if (!active) {
        setSelectedPolygonId(null);
        return;
      }

      const pointer = stage.getPointerPosition();
      if (!pointer) {
        return;
      }

      const scale = stageScale || 1;
      const pointerX = clamp(pointer.x / scale, 0, stageWidth);
      const pointerY = clamp(pointer.y / scale, 0, stageHeight);

      if (active.points.length >= 3) {
        const firstPoint = active.points[0];
        if (firstPoint) {
          const distance = Math.hypot(pointerX - firstPoint.x, pointerY - firstPoint.y);
          const closeThreshold = 14;
          if (distance <= closeThreshold) {
            updatePolygon(active.id, (polygon) => ({
              ...polygon,
              completed: true,
            }));
            setActivePolygonId(null);
            return;
          }
        }
      }

      const newPoint = {
        x: pointerX,
        y: pointerY,
      };

      updatePolygon(active.id, (polygon) => ({
        ...polygon,
        points: [...polygon.points, newPoint],
      }));
    },
    [activePolygon, isEditable, polygons, stageHeight, stageScale, stageWidth, updatePolygon],
  );

  const handlePointDragMove = useCallback(
    (polygonId: string, pointIndex: number, point: Konva.Node) => {
      if (!isEditable) {
        return;
      }

      const scale = stageScale || 1;
      const x = clamp(point.x() / scale, 0, stageWidth);
      const y = clamp(point.y() / scale, 0, stageHeight);

      updatePolygon(polygonId, (polygon) => {
        if (!polygon.points[pointIndex]) {
          return polygon;
        }
        const nextPoints = polygon.points.map((entry, index) => (index === pointIndex ? { x, y } : entry));
        return {
          ...polygon,
          points: nextPoints,
        };
      });
    },
    [isEditable, stageHeight, stageScale, stageWidth, updatePolygon],
  );

  const handleAddPolygon = useCallback(() => {
    if (!isEditable) {
      return;
    }
    const nextIndex = polygons.length + 1;
    const polygon: CameraAnnotationPolygon = {
      id: createPolygonId(),
      name: `Parking spot ${nextIndex}`,
      color: colorForPolygonType('regular'),
      type: 'regular',
      points: [],
      completed: false,
    };
    setPolygons((prev) => [...prev, polygon]);
    setActivePolygonId(polygon.id);
    setSelectedPolygonId(polygon.id);
  }, [isEditable, polygons.length]);

  const handleUndoLastPoint = useCallback(() => {
    if (!activePolygon || !isEditable) {
      return;
    }
    if (activePolygon.points.length === 0) {
      return;
    }
    updatePolygon(activePolygon.id, (polygon) => {
      if (polygon.points.length === 0) {
        return polygon;
      }
      const nextPoints = polygon.points.slice(0, -1);
      return {
        ...polygon,
        points: nextPoints,
      };
    });
  }, [activePolygon, isEditable, updatePolygon]);

  const handleCancelPolygon = useCallback(() => {
    if (!activePolygon || !isEditable) {
      return;
    }
    setPolygons((prev) => prev.filter((polygon) => polygon.id !== activePolygon.id));
    if (selectedPolygonId === activePolygon.id) {
      setSelectedPolygonId(null);
    }
    setActivePolygonId(null);
  }, [activePolygon, isEditable, selectedPolygonId]);

  const handleDeletePolygon = useCallback(
    (polygonId: string) => {
      if (!isEditable) {
        return;
      }
      setPolygons((prev) => prev.filter((polygon) => polygon.id !== polygonId));
      if (activePolygonId === polygonId) {
        setActivePolygonId(null);
      }
      if (selectedPolygonId === polygonId) {
        setSelectedPolygonId(null);
      }
    },
    [activePolygonId, isEditable, selectedPolygonId],
  );

  const handleExportJson = useCallback(async () => {
    if (!polygons.length) {
      setExportFeedback('Brak poligonów do eksportu.');
      if (exportFeedbackTimeoutRef.current) {
        window.clearTimeout(exportFeedbackTimeoutRef.current);
      }
      exportFeedbackTimeoutRef.current = window.setTimeout(() => setExportFeedback(null), 2500);
      return;
    }

    const payload = buildExportPayload(polygons, image, new Date().toISOString());
    const jsonString = JSON.stringify(payload, null, 2);

    let copied = false;
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(jsonString);
        copied = true;
      } catch (error) {
        console.warn('Nie udało się skopiować JSON do schowka', error);
      }
    }

    try {
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = jsonDownloadFileName(cityId, spotId);
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Nie udało się pobrać pliku JSON', error);
    }

    setExportFeedback(copied ? 'JSON zapisany i skopiowany do schowka.' : 'JSON zapisany w pliku.');
    if (exportFeedbackTimeoutRef.current) {
      window.clearTimeout(exportFeedbackTimeoutRef.current);
    }
    exportFeedbackTimeoutRef.current = window.setTimeout(() => setExportFeedback(null), 3500);
  }, [cityId, image, polygons, spotId]);

  const handlePolygonFieldChange = useCallback(
    (polygonId: string, value: string) => {
      updatePolygon(polygonId, (polygon) => ({ ...polygon, name: value }));
    },
    [updatePolygon],
  );
  const handlePolygonTypeChange = useCallback(
    (polygonId: string, value: string) => {
      if (!isCameraPolygonType(value)) {
        return;
      }
      const nextType = value as CameraPolygonType;
      updatePolygon(polygonId, (polygon) => ({
        ...polygon,
        type: nextType,
        color: colorForPolygonType(nextType),
      }));
    },
    [updatePolygon],
  );
  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col">
          <span className="w-fit rounded-full border border-emerald-400/70 bg-emerald-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-600 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-300">
            Kamera
          </span>
          <h2 className="mt-2 text-lg font-semibold uppercase tracking-[0.28em] text-slate-600 dark:text-slate-200">{image.name}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isEditable ? (
            <>
              <button
                type="button"
                onClick={handleAddPolygon}
                className="rounded-lg border border-emerald-500 bg-emerald-500 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-emerald-400 dark:border-emerald-400 dark:bg-emerald-400"
              >
                Dodaj poligon
              </button>
              <button
                type="button"
                onClick={handleUndoLastPoint}
                disabled={!activePolygon || activePolygon.points.length === 0}
                className="rounded-lg border border-slate-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 dark:border-slate-300 dark:text-slate-200 dark:hover:bg-slate-300/20 dark:disabled:border-white/10 dark:disabled:text-slate-500"
              >
                Cofnij punkt
              </button>
              <button
                type="button"
                onClick={handleCancelPolygon}
                disabled={!activePolygon}
                className="rounded-lg border border-rose-300 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-500 transition hover:bg-rose-500 hover:text-white disabled:cursor-not-allowed disabled:border-rose-200 disabled:text-rose-300 dark:border-rose-300 dark:text-rose-300 dark:hover:bg-rose-400/20 dark:disabled:border-rose-200/50 dark:disabled:text-rose-200"
              >
                Anuluj rysowanie
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className={`flex flex-col gap-6${isEditable ? ' lg:flex-row lg:items-start' : ''}`}>
        <div className="relative flex-1">
          {isDrawingInProgress ? (
            <div className="pointer-events-none absolute left-3 top-3 z-10 flex flex-wrap items-center gap-2 rounded-full bg-white/90 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-600 shadow-sm dark:bg-slate-900/80 dark:text-slate-200">
              <span className="text-[10px] tracking-[0.2em]">
                Rysujesz: {activePolygon?.name} ({activePolygon?.points.length ?? 0} pkt)
              </span>
              <button
                type="button"
                onClick={handleCancelPolygon}
                className="pointer-events-auto rounded-full border border-rose-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-rose-500 transition hover:bg-rose-500 hover:text-white dark:border-rose-300 dark:text-rose-200 dark:hover:bg-rose-400/20"
              >
                Anuluj
              </button>
            </div>
          ) : null}
          <div
            ref={containerRef}
            className="relative flex w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-emerald-300/50 bg-slate-950/80 shadow-inner transition dark:border-emerald-400/40"
            style={{
              height: `${Math.max(stageHeight * stageScale, 320)}px`,
              maxHeight: `${maxStageHeight}px`,
              touchAction: isEditable ? 'none' : 'pinch-zoom',
            }}
          >
            {imageElement ? (
              <Stage
                ref={stageRef}
                width={stageWidth * stageScale}
                height={stageHeight * stageScale}
                onMouseDown={handleStagePointerDown}
                onTouchStart={handleStagePointerDown}
              >
                <Layer listening={false}>
                  <KonvaImage
                    image={imageElement}
                    width={stageWidth * stageScale}
                    height={stageHeight * stageScale}
                    listening={false}
                    opacity={0.98}
                    name="camera-background"
                  />
                </Layer>
                <Layer>
                  {polygons.map((polygon) => {
                    const isSelected = selectedPolygonId === polygon.id;
                    const isActive = activePolygonId === polygon.id && !polygon.completed;
                    const centroid = centroidOf(polygon.points);
                    const points = polygon.points.flatMap((point) => [point.x * stageScale, point.y * stageScale]);
                    const strokeWidth = Math.max(1, 2 * stageScale);
                    const dashLength = Math.max(4, 8 * stageScale);
                    const dashGap = Math.max(3, 6 * stageScale);
                    const labelWidth = Math.max(96 * stageScale, 72);
                    const fontSize = Math.max(12 * stageScale, 10);

                    return (
                      <Group key={polygon.id}>
                        <Line
                          data-polygon-id={polygon.id}
                          points={points}
                          closed={polygon.completed}
                          stroke={polygon.color}
                          strokeWidth={strokeWidth}
                          lineCap="round"
                          lineJoin="round"
                          dash={polygon.completed ? undefined : [dashLength, dashGap]}
                          opacity={polygon.completed ? 0.45 : 0.8}
                          fill={polygon.completed ? polygon.color : undefined}
                        />
                        {centroid ? (
                          <Text
                            text={polygon.name}
                            x={centroid.x * stageScale - labelWidth / 2}
                            y={centroid.y * stageScale - fontSize * 1.1}
                            width={labelWidth}
                            height={fontSize * 2}
                            align="center"
                            fill="#ffffff"
                            shadowColor="rgba(15,23,42,0.6)"
                            shadowBlur={4}
                            shadowOffset={{ x: 0, y: 1 }}
                            fontSize={fontSize}
                            listening={false}
                          />
                        ) : null}
                        {polygon.points.map((point, index) => (
                          <Circle
                            key={`${polygon.id}-point-${index}`}
                            data-polygon-id={polygon.id}
                            data-point-index={index}
                            x={point.x * stageScale}
                            y={point.y * stageScale}
                            radius={Math.max(6, 10 * stageScale)}
                            stroke={polygon.color}
                            strokeWidth={Math.max(1, 1.5 * stageScale)}
                            fill="#ffffff"
                            opacity={isSelected || isActive ? 0.9 : 0.4}
                            draggable={isEditable}
                            onDragMove={(dragEvent) => handlePointDragMove(polygon.id, index, dragEvent.target)}
                            onMouseDown={() => {
                              setSelectedPolygonId(polygon.id);
                              if (isEditable && !polygon.completed && !(index === 0 && polygon.points.length >= 3)) {
                                setActivePolygonId(polygon.id);
                              }
                            }}
                            onTouchStart={() => {
                              setSelectedPolygonId(polygon.id);
                              if (isEditable && !polygon.completed && !(index === 0 && polygon.points.length >= 3)) {
                                setActivePolygonId(polygon.id);
                              }
                            }}
                          />
                        ))}
                      </Group>
                    );
                  })}
                </Layer>
              </Stage>
            ) : (
              <div className="flex min-h-[360px] items-center justify-center p-10 text-center text-sm uppercase tracking-[0.35em] text-slate-300">
                Ładowanie podglądu kamery...
              </div>
            )}
          </div>
        </div>

        {isEditable ? (
          <aside className="flex w-full flex-col gap-4 lg:w-80 lg:max-h-[520px] lg:overflow-y-auto">
            <div className="rounded-xl border border-slate-200 bg-white/80 p-4 text-[10px] uppercase tracking-[0.3em] text-slate-500 shadow-inner dark:border-white/10 dark:bg-white/10 dark:text-slate-300/80">
              <span className="font-semibold text-slate-700 dark:text-slate-100">
                {polygons.length} poligon{polygons.length === 1 ? '' : 'ów'}
              </span>
              {activePolygon ? (
                <p className="mt-2 text-[10px] normal-case tracking-[0.1em] text-emerald-600 dark:text-emerald-300">
                  Poligon w trakcie rysowania: {activePolygon.name} ({activePolygon.points.length} pkt)
                </p>
              ) : null}
              {exportFeedback ? <p className="mt-2 text-[10px] normal-case tracking-[0.1em] text-emerald-500 dark:text-emerald-300">{exportFeedback}</p> : null}
            </div>

            <button
              type="button"
              onClick={handleExportJson}
              className="rounded-lg border border-slate-300 px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-600 transition hover:bg-slate-600 hover:text-white dark:border-slate-300 dark:text-slate-200 dark:hover:bg-slate-300/20"
            >
              Eksportuj JSON
            </button>

            <div className="flex flex-col gap-3 overflow-y-auto rounded-xl border border-slate-200 bg-white/80 p-3 shadow-inner dark:border-white/10 dark:bg-white/10">
              {polygons.length === 0 ? (
                <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/80">Brak zapisanych poligonów</p>
              ) : null}
              {polygons.map((polygon) => {
                const isSelected = selectedPolygonId === polygon.id;
                const labelId = `polygon-${polygon.id}`;
                return (
                  <div
                    key={polygon.id}
                    className={`rounded-lg border p-3 transition ${
                      isSelected
                        ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-300 dark:bg-emerald-400/10'
                        : 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/5'
                    }`}
                  >
                    <button type="button" onClick={() => setSelectedPolygonId(polygon.id)} className="flex w-full items-center justify-between gap-2 text-left">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-4 w-4 rounded-full border border-slate-300" style={{ backgroundColor: polygon.color }} />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-600 dark:text-slate-200">{polygon.name}</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400 dark:text-slate-300/80">{polygon.points.length} pkt</span>
                    </button>

                    {isSelected ? (
                      <div className="mt-3 flex flex-col gap-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
                        <label htmlFor={`${labelId}-name`} className="flex flex-col gap-1">
                          <span>Nazwa</span>
                          <input
                            id={`${labelId}-name`}
                            type="text"
                            value={polygon.name}
                            onChange={(event) => handlePolygonFieldChange(polygon.id, event.target.value)}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:focus:border-emerald-300"
                          />
                        </label>
                        <label htmlFor={`${labelId}-type`} className="flex flex-col gap-1">
                          <span>Typ</span>
                          <select
                            id={`${labelId}-type`}
                            value={polygon.type}
                            onChange={(event) => handlePolygonTypeChange(polygon.id, event.target.value)}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-emerald-400/60 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:border-emerald-300"
                          >
                            {POLYGON_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={() => handleDeletePolygon(polygon.id)}
                          className="w-full rounded-lg border border-rose-300 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-500 transition hover:bg-rose-500 hover:text-white dark:border-rose-300 dark:text-rose-300 dark:hover:bg-rose-400/20"
                        >
                          Usuń poligon
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
